import { computed, EnvironmentInjector, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';
import { ChainNetworkMetadata, CurrencyMetadata, NetworkMetadataResponse } from '../models/network-metadata.model';
import { CurrencyOption } from '../models/wallet.model';
import { AUTH_CONFIG, type AuthConfig } from '../features/auth/auth.config';
import { APP_NAME } from './app-context.service';
import { StorageService } from './storage.service';

export const NETWORK_METADATA_CACHE_KEY = 'network_metadata';

const sortRank = (item: { sortOrder: number }) => item.sortOrder || Number.MAX_SAFE_INTEGER;
const bySortOrder = (a: { sortOrder: number }, b: { sortOrder: number }) => sortRank(a) - sortRank(b);

@Injectable({
  providedIn: 'root',
})
export class NetworkMetadataService {
  private readonly http = inject(HttpClient);
  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly storage = inject(StorageService);
  private readonly injector = inject(EnvironmentInjector);

  private readonly loading = signal(false);

  readonly isLoading = this.loading.asReadonly();

  private readonly loadError = signal<string | null>(null);

  readonly error = this.loadError.asReadonly();
  readonly networks = computed(() =>
    this.entries().filter((network): network is ChainNetworkMetadata => network.networkType !== null),
  );
  readonly walletCreationNetworks = computed(() => this.entries().filter((network) => network.forWalletCreation));
  readonly currencies = computed(() => [...(this.metadata()?.currencies ?? [])].sort(bySortOrder));
  readonly filterFields = computed(() =>
    this.networks().map((network) => ({ name: network.name, value: network.networkType })),
  );
  readonly options = computed(() =>
    this.networks().map((network) => ({ value: network.networkType, label: network.name })),
  );
  readonly optionsWithPlaceholder = computed(() => [
    { value: null as number | null, label: 'Select Blockchain' },
    ...this.options(),
  ]);

  private readonly onlyStableCoins = inject(APP_NAME, { optional: true }) === 'merchant';
  private readonly metadata = signal<NetworkMetadataResponse | null>(this.readCache());
  private isFresh = false;
  private isFetching = false;
  private pending: Observable<NetworkMetadataResponse> | null = null;
  private generation = 0;
  private readonly entries = computed(() => [...(this.metadata()?.networks ?? [])].sort(bySortOrder));

  load(): void {
    if (this.isFresh || this.isFetching) return;

    const generation = this.generation;
    const isCurrent = () => generation === this.generation;
    this.isFetching = true;
    this.loading.set(!this.metadata() && !this.loadError());
    this.pending = this.fetch(this.onlyStableCoins).pipe(
      finalize(() => {
        if (!isCurrent()) return;
        this.isFetching = false;
        this.loading.set(false);
      }),
      shareReplay(1),
    );
    this.pending.subscribe({
      next: (response) => {
        if (!isCurrent()) return;
        this.isFresh = true;
        this.loadError.set(null);
        this.metadata.set(response);
        this.storage.setItem(NETWORK_METADATA_CACHE_KEY, JSON.stringify(response));
      },
      error: (err) => {
        if (!isCurrent()) return;
        if (this.metadata()) {
          console.warn('Network metadata refresh failed; using the cached copy', err);
          return;
        }
        if (!this.loadError()) this.showLoadErrorToast();
        this.loadError.set('Networks and currencies could not be loaded.');
      },
    });
  }

  whenLoaded(): Observable<void> {
    this.load();
    if (!this.isFetching || !this.pending) return of(undefined);
    return this.pending.pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  clear(): void {
    this.generation++;
    this.isFresh = false;
    this.isFetching = false;
    this.pending = null;
    this.loading.set(false);
    this.metadata.set(null);
    this.loadError.set(null);
    this.storage.removeItem(NETWORK_METADATA_CACHE_KEY);
  }

  network(networkType: number): ChainNetworkMetadata | undefined {
    return this.networks().find((network) => network.networkType === networkType);
  }

  currency(id: string): CurrencyMetadata | undefined {
    return this.currencies().find((currency) => currency.id === id);
  }

  networkOf(currency: CurrencyMetadata): ChainNetworkMetadata | undefined {
    return this.networks().find((network) => network.id === currency.networkId);
  }

  currenciesOf(networkType: number): CurrencyMetadata[] {
    const network = this.network(networkType);
    return network ? this.currencies().filter((currency) => currency.networkId === network.id) : [];
  }

  currencyOptionsOf(networkType: number): CurrencyOption[] {
    return this.currenciesOf(networkType).map((currency) => ({
      value: currency.symbol.toLowerCase(),
      label: currency.symbol,
      id: currency.id,
      precision: currency.precision,
    }));
  }

  nativeCurrencyOf(networkType: number): CurrencyMetadata | undefined {
    const nativeCurrencyId = this.network(networkType)?.nativeCurrencyId;
    return nativeCurrencyId ? this.currency(nativeCurrencyId) : undefined;
  }

  private showLoadErrorToast(): void {
    import('@olv/ui-shared/ui-shared/toast/toast.service')
      .then(({ ToastService }) => {
        if (this.injector.destroyed) return;
        this.injector.get(ToastService).error('Networks and currencies could not be loaded. Please try again.');
      })
      .catch((error) => console.error('Failed to load the toast service:', error));
  }

  private readCache(): NetworkMetadataResponse | null {
    try {
      const cached = JSON.parse(this.storage.getItem(NETWORK_METADATA_CACHE_KEY) ?? 'null');
      return Array.isArray(cached?.networks) && Array.isArray(cached?.currencies) ? cached : null;
    } catch {
      return null;
    }
  }

  private fetch(onlyStableCoins: boolean): Observable<NetworkMetadataResponse> {
    const params = onlyStableCoins ? new HttpParams().set('onlyStableCoins', true) : undefined;
    return this.http.get<NetworkMetadataResponse>(`${this.cfg.baseUrl}/api/Currency/metadata`, { params });
  }
}
